import { createFactory } from 'react';
import _ from 'lodash';

// redux action creators for persistence
import * as asyncActionCreators from '../../redux/actionCreators/async';
import * as syncActionCreators from '../../redux/actionCreators/sync';

// Profile components
import PrelimInterview from './Steps/Profile/f_PrelimInterview';
import ExplanationOfWahve from './Steps/Profile/f_ExplanationOfWahve';
import ConclusionToTheInterview from './Steps/Profile/c_ConclusionToTheInterview';

// Review components
import PreliminaryProfile from './Steps/Review/f_PreliminaryProfile';
import AssessmentsTable from '../admin/Applicant/AssessmentsTable';

// Interview components
import Introduction from './Steps/Interview/f_Introduction';
import ApplicationAssessmentsInstructions from './Steps/Interview/f_ApplicationAssessmentsInstructions';
import StatusDetermination from './Steps/Interview/f_StatusDetermination';
import MedicareSocialSecurity from './Steps/Interview/f_MedicareSocialSecurity';
import BehavioralInterview from './Steps/Interview/f_BehavioralInterview';
import DeterminationOfStatus from './Steps/Interview/f_DeterminationOfStatus';
import WorkObjectives from './Steps/Interview/f_WorkObjectives';
import Ratings from './Steps/Interview/f_Ratings';

// from applicant side
import AvailabilityInformation from '../r_ApplicantDashboard/f_Application/r_ApplicationForm/Preferences/c_AvailabilityInformation';
import HomeOfficeSetup from '../r_ApplicantDashboard/f_Application/r_ApplicationForm/Preferences/c_HomeOfficeSetup';
import WorkExperience from '../r_ApplicantDashboard/f_Application/r_ApplicationForm/Experience/c_WorkExperience';

import ProfessionalDesignationsSummary from '../applicant/ProfessionalDesignationsSummary';
import ProfessionalAssociationsSummary from '../applicant/ProfessionalAssociationsSummary';
import LicensesSummary from '../applicant/LicensesSummary';
import SkillsSummary from '../applicant/SkillsSummary';
import EmploymentGapsSummary from '../applicant/EmploymentGapsSummary';

// Model schemas
import {getSchema as getProfileSchema} from '../../models/applicantProfile';
import {getSchema as getWorkExperienceSchema} from '../../models/applicantWorkExperience';

const profileSchema = getProfileSchema();
const workExperienceSchema = getWorkExperienceSchema();

// retrieves current component
export function getCurrent(activeColumnNav, activeRowSubNav) {
  // look up column on 'lookup' object
  let lookupVal = lookup[activeColumnNav];

  // corresponding string
  let viewModel = activeColumnNav;

  // some components are nested, others are not, this handles that
  if(lookupVal && activeRowSubNav) {
    lookupVal = lookupVal[activeRowSubNav];
    viewModel += `.${activeRowSubNav}`;
  }
  return {...lookupVal, viewModel};
}

export const lookup = {
  Profile: {
    'Preliminary Information': {
      component: createFactory(PrelimInterview),
      model: 'profile',
      validator: () => true
    },
    'Explanation of Wahve': {
      component: createFactory(ExplanationOfWahve),
      model: 'profile',
      validator: () => true
    },
    'Conclusion to the Interview': {
      component: createFactory(ConclusionToTheInterview),
      model: 'profile',
      validator: () => true
    },
  },
  Review: {
    'Preliminary Profile': {
      component: createFactory(PreliminaryProfile),
      model: 'profile',
      validator: () => true
    },
    Application: {
      component: createFactory(SkillsSummary),
      model: 'skills',
      validation: {},
      validator: () => true
    },
    Assessments: {
      component: createFactory(AssessmentsTable),
      model: 'assessments',
      validator: () => true
    }
  },
  Interview: {
    'Application Assessments Instructions': {
      component: ApplicationAssessmentsInstructions,
      model: 'profile',
      validator: () => true
    },
    Assessments: {
      component: createFactory(AssessmentsTable),
      model: 'assessments',
      validator: () => true,
      saveFunc: function(applicant, group, arrayIndex) {
        const actions = [];
        applicant.assessments && actions.push(asyncActionCreators.updateApplicantAssessments(applicant, applicant.assessments, group, arrayIndex));
        applicant.ratings && actions.push(asyncActionCreators.updateApplicantInterviewRatings(applicant, applicant.ratings, group, arrayIndex));
        applicant.profile.viewState && actions.push(asyncActionCreators.updateApplicantAdmin(applicant, group, arrayIndex));
        return actions;
      }
    },
    'Preferences Availability': {
      component: createFactory(AvailabilityInformation),
      model: 'profile',
      validation: profileSchema,
      validator: () => true
    },
    'Home Office Setup': {
      component: createFactory(HomeOfficeSetup),
      model: 'profile',
      validation: {},
      validator: () => true
    },
    'Work Experience': {
      component: createFactory(WorkExperience),
      model: 'workExperiences',
      validation: workExperienceSchema,
      validator: () => true,
      saveFunc: function(newstate, group, arrayIndex) {
        const actions = [];
        const updateViewState = _.get(newstate, 'profile.viewState', null) ? true : false;
        const updateProfile =  !_.isEmpty(_.omit(newstate.profile, ['viewState']));
        const updateWorkExperience = !_.isNil(newstate.workExperiences);
        (updateWorkExperience) && actions.push(asyncActionCreators.persistListUpdate('workExperiences', 'workExperiences', newstate));
        (updateViewState || updateProfile) && actions.push(asyncActionCreators.updateApplicantAdmin(newstate, group, arrayIndex));
        (updateWorkExperience || updateViewState || updateProfile) && actions.push(syncActionCreators.updateApplicant(newstate));
        return actions;
      }
    },
    'Employment Gaps': {
      component: createFactory(EmploymentGapsSummary),
      model: 'employmentGaps',
      validation: {},
      validator: () => true
    },
    Skills: {
      component: createFactory(SkillsSummary),
      model: 'skills',
      validation: {},
      validator: () => true
    },
    Licenses: {
      component: createFactory(LicensesSummary),
      model: 'licenses',
      validation: {},
      validator: () => true
    },
    'Professional Designations': {
      component: createFactory(ProfessionalDesignationsSummary),
      model: 'designations',
      validation: {},
      validator: () => true
    },
    'Professional Associations': {
      component: createFactory(ProfessionalAssociationsSummary),
      model: 'associations',
      validation: {},
      validator: () => true
    },
    Introduction: {
      component: Introduction,
      model: 'profile',
      validator: () => true
    },
    'Status Determination': {
      component: StatusDetermination,
      model: 'profile',
      validator: () => true
    },
    'Medicare and Social Security': {
      component: MedicareSocialSecurity,
      model: 'profile',
      validator: () => true
    },
    'Behavioral Interview': {
      component: createFactory(BehavioralInterview),
      model: 'behavioralAnswers',
      validator: function(question, value) {
        let errors = [];
        if (question && question.required && _.isEmpty(value)) {
          errors.push('This field is requried.');
        }
        return errors;
      },
      saveFunc: function(applicant, group, arrayIndex) {
        const actions = [];
        applicant.behavioralAnswers && actions.push(asyncActionCreators.updateApplicantBehavioralAnswers(applicant, applicant.behavioralAnswers, group, arrayIndex));
        applicant.profile.viewState && actions.push(asyncActionCreators.updateApplicantAdmin(applicant, group, arrayIndex));
        return actions;
      }
    },
    'Work Objectives': {
      component:  createFactory(WorkObjectives),
      model: 'workObjectives',
      validator: function(question, value) {
        let errors = [];
        if (question && question.required && _.isEmpty(value)) {
          errors.push('This field is requried.');
        } else if (question && value.length > 4000) {
          errors.push('Max length is 4000 characters.')
        }

        return errors;
      },
      saveFunc: function(applicant, group, arrayIndex) {
        const actions = [];
        applicant.workObjectives && actions.push(asyncActionCreators.updateApplicantWorkObjectives(applicant, applicant.workObjectives, group, arrayIndex));
        applicant.profile.viewState && actions.push(asyncActionCreators.updateApplicantAdmin(applicant, group, arrayIndex));
        return actions;
      }
    },
    Ratings: {
      component: createFactory(Ratings),
      model: 'ratings',
      validator: () => true,
      saveFunc: function(applicant, group, arrayIndex) {
        const actions = [];
        applicant.ratings && actions.push(asyncActionCreators.updateApplicantInterviewRatings(applicant, applicant.ratings, group, arrayIndex));
        applicant.profile.viewState && actions.push(asyncActionCreators.updateApplicantAdmin(applicant, group, arrayIndex));
        return actions;
      }
    },
    'Determination of Status': {
      component: createFactory(DeterminationOfStatus),
      model: 'profile',
      validator: () => true
    }
  }
};



// WEBPACK FOOTER //
// ./shared/react/r_ExpandedCard/componentLookup.js